import React, { useState, useEffect } from "react";
import Header from "@/components/layout/Header";
import AuthContext, { useAuth } from "@/hooks/useAuth";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import authService from "@/services/authService";
import setupAxiosInterceptors from "@/services/axiosInterceptor";
import ErrorModal from "@/components/error/ErrorModal";
import useErrorStore from "@/store/errorStore";
import "./App.css";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";

import {
  Trash2,
  Star,
  ShoppingCart,
  User,
  Package,
  MessageSquare,
  Settings,
  BarChart3,
  Eye,
  LogOut,
  Menu,
  X,
  BellRing,
  Store,
  ListTodo,
  UserRoundCog,
  TableOfContents,
  CloudUpload,
  http,
  MapPin,
  CreditCard,
  Warehouse,
  QrCode,
  Wrench,
  Package2,
  Ticket,
} from "lucide-react";
import IngredientsManagementModal from "@/components/ingredients/IngredientsManagementModal";
import UploadsManagementModal from "@/components/uploads/UploadsManagementModal";
import OrdersManagement from "@/components/orders/OrdersManagement";
import ProductsManagement from "@/components/products/ProductsManagement";
import CategoriesManagement from "@/components/categories/CategoriesManagement";
import EtiqueLabelGenerator from "@/components/etique/EtiqueLabelGenerator";
import Dashboard from "@/components/Dashboard";
import UsersManagement from "@/components/users/UsersManagement";
import AddressesManagement from "@/components/addresses/AddressesManagement";
import PaymentCategoriesManagement from "@/components/payments/PaymentCategoriesManagement";
import StorageLocationsManagement from "@/components/storage-locations/StorageLocationsManagement";
import PixManagement from "@/components/pix/PixManagement";
import CompaniesManagement from "@/components/companies/CompaniesManagement";
import EquipmentsManagement from "@/components/equipments/EquipmentsManagement";
import StocksManagement from "@/components/stock/StocksManagement";
import CouponsManagement from "@/components/coupons/CouponsManagement";
import ClientsManagement from "@/components/clients/ClientsManagement";
import PrintOrderReceipt from "@/components/orders/PrintOrderReceipt";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Setup axios interceptors once when app loads
    setupAxiosInterceptors();

    // Check if user has a valid token
    if (authService.isAuthenticated()) {
      // Validate token is not expired first
      if (authService.isTokenValid()) {
        fetchUserInfo();
      } else {
        // Token expired, clear it
        authService.logout();
        setUser(null);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await authService.getCurrentUser();
      setUser(response);
    } catch (error) {
      // Token is invalid (401), clear and show login
      authService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      // Após login, buscar dados completos do usuário
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return true;
    } catch (error) {
      console.error("Falha no login:", error.message);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(username, password);
    } catch (error) {
      let errorMessage = "Credenciais inválidas";

      if (error.response) {
        if (error.response.status === 401) {
          errorMessage = "Usuário ou senha incorretos";
        } else if (error.response.status === 500) {
          errorMessage = "Erro no servidor. Tente novamente mais tarde.";
        } else {
          errorMessage = error.response.data?.detail || "Erro ao fazer login";
        }
      } else if (error.request) {
        errorMessage = "Sem resposta do servidor. Verifique sua conexão.";
      } else {
        errorMessage = "Erro ao processar login: " + error.message;
      }

      setError(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-red-100 to-red-50"
      style={{
        background:
          "linear-gradient(to bottom right, #fef2f2, #fee2e2, #fef2f2)",
      }}
    >
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto mb-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: "#c9332C" }}
            >
              <Package className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle
            className="text-3xl font-bold"
            style={{ color: "#c9332C" }}
          >
            Delivery Bonfim
          </CardTitle>
          <CardDescription className="text-gray-600 text-lg">
            Gerenciador de cardápio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="username"
                className="text-sm font-medium text-gray-700"
              >
                E-mail ou Usuário
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-12 border-gray-200 focus:border-amber-500 focus:ring-amber-500"
                placeholder="Digite seu e-mail ou usuário"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-sm font-medium text-gray-700"
              >
                Senha
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 border-gray-200"
                style={{ "--tw-ring-color": "#c9332C" }}
                onFocus={(e) => (e.target.style.borderColor = "#c9332C")}
                onBlur={(e) => (e.target.style.borderColor = "")}
                placeholder="Digite sua senha"
              />
            </div>
            {error && (
              <div className="text-red-500 text-sm text-center bg-red-50 py-2 px-3 rounded-lg">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-white font-semibold rounded-lg shadow-lg transition-all duration-300"
              style={{ background: "#c9332C" }}
              onMouseEnter={(e) => (e.target.style.background = "#a02820")}
              onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen }) => {
  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "products", label: "Produtos", icon: Package },
    { id: "orders", label: "Pedidos", icon: ShoppingCart },
    { id: "pix", label: "Pagamentos", icon: QrCode },
    { id: "companies", label: "Empresas", icon: Store },
    { id: "stocks", label: "Estoque", icon: Package2 },
    { id: "storage-locations", label: "Armazenamento", icon: Warehouse },
    { id: "equipments", label: "Equipamentos", icon: Wrench },
    { id: "etiquetas", label: "Etiquetas", icon: MessageSquare },
    { id: "categories", label: "Categorias", icon: TableOfContents },
    { id: "coupons", label: "Cupons", icon: Ticket },
    { id: "ingredients", label: "Ingredientes", icon: ListTodo },
    { id: "uploads", label: "Upload", icon: CloudUpload },
    { id: "users", label: "Usuarios", icon: UserRoundCog },
    { id: "clients", label: "Clientes", icon: User },
    { id: "addresses", label: "Endereços", icon: MapPin },
  ];

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div
        className={`${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } fixed lg:static lg:translate-x-0 inset-y-0 left-0 w-64 bg-white shadow-lg transition-transform duration-300 ease-in-out z-50 overflow-y-auto`}
      >
        <div className="p-3 md:p-4 pt-16 lg:pt-4">
          <nav className="space-y-1 md:space-y-2">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                  }}
                  className={`w-full flex items-center space-x-2 md:space-x-3 px-3 md:px-4 py-2 md:py-3 rounded-lg text-left transition-all duration-200 text-sm md:text-base ${
                    activeTab === item.id
                      ? "text-white shadow-lg"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  style={activeTab === item.id ? { background: "#c9332C" } : {}}
                >
                  <IconComponent className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                  <span className="font-medium truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

// Dashboard Component
const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orderFilterStatus, setOrderFilterStatus] = useState("");
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  useEffect(() => {
    // Carrega produtos ao abrir painel
    fetchProducts();
  }, []);

  useEffect(() => {
    // Limpa o filtro de status quando mudar de tab (exceto quando vai para orders)
    if (activeTab !== "orders") {
      setOrderFilterStatus("");
    }
  }, [activeTab]);

  // Função para trocar de página com loading
  const handleTabChange = (tabId) => {
    setIsLoadingPage(true);
    setActiveTab(tabId);
    setSidebarOpen(false);

    // Simula tempo de carregamento para transição suave
    setTimeout(() => {
      setIsLoadingPage(false);
    }, 300);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/admin/all`);
      const payload = response.data?.data ?? response.data ?? [];
      setProducts(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setProducts([]);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard />;
      case "products":
        return <ProductsManagement />;
      case "categories":
        return <CategoriesManagement />;
      case "coupons":
        return <CouponsManagement />;
      case "ingredients":
        return <IngredientsManagement />;
      case "uploads":
        return <UploadsManagement />;
      case "users":
        return <UsersManagement />;
      case "clients":
        return <ClientsManagement />;
      case "addresses":
        return <AddressesManagement />;
      case "storage-locations":
        return <StorageLocationsManagement />;
      case "equipments":
        return <EquipmentsManagement />;
      case "stocks":
        return <StocksManagement />;
      case "orders":
        return <OrdersManagement initialStatus={orderFilterStatus} />;
      case "payments":
        return <PaymentCategoriesManagement />;
      case "pix":
        return <PixManagement />;
      case "companies":
        return <CompaniesManagement />;
      case "settings":
        return <SettingsManagement />;
      case "etiquetas":
        return <EtiqueLabelGenerator products={products} />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        setOrderFilterStatus={setOrderFilterStatus}
      />
      <div className="flex flex-col lg:flex-row">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <main className="flex-1 p-4 md:p-6 lg:p-8 w-full overflow-x-hidden">
          {isLoadingPage ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-amber-600 mb-4"></div>
                <p className="text-gray-600 font-medium">Carregando...</p>
              </div>
            </div>
          ) : (
            renderContent()
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 px-6 mt-auto">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <p>
            © {new Date().getFullYear()} Bonfim Gourmet. Todos os direitos
            reservados.
          </p>
          <p className="font-medium text-amber-600">
            v{process.env.REACT_APP_VERSION || "1.0.0"}
          </p>
        </div>
      </footer>
    </div>
  );
};

// (moved into separate component file: src/components/categories/CategoriesManagement.jsx)

// Categories Management Component
const IngredientsManagement = () => {
  return <IngredientsManagementModal />;
};

// Categories Management Component
const UploadsManagement = () => {
  return (
    <div className="p-4">
      <UploadsManagementModal
        onSelect={(url, name) => {
          // Exemplo: integra no form de produto
          // setValue("image_url", url);
          console.log("Selecionada:", url, name);
        }}
      />
    </div>
  );
};

// Comments Management Component
const CommentsManagement = () => {
  const [comments, setComments] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchComments();
    fetchProducts();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/admin/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/admin/all`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleApprove = async (commentId) => {
    try {
      await axios.put(`${API}/admin/comments/${commentId}/approve`);
      fetchComments();
    } catch (error) {
      console.error("Error approving comment:", error);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm("Tem certeza que deseja excluir este comentário?")) {
      try {
        await axios.delete(`${API}/admin/comments/${commentId}`);
        fetchComments();
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? "text-yellow-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">
        Gestão de Comentários
      </h2>

      <div className="space-y-4">
        {comments.map((comment) => {
          const product = products.find((p) => p.id === comment.product_id);
          return (
            <Card key={comment.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold">{comment.user_name}</h4>
                      <div className="flex">{renderStars(comment.rating)}</div>
                      <Badge
                        variant={comment.is_approved ? "default" : "secondary"}
                      >
                        {comment.is_approved ? "Aprovado" : "Pendente"}
                      </Badge>
                    </div>
                    <p className="text-gray-600 mb-2">{comment.content}</p>
                    <div className="text-sm text-gray-500">
                      <p>
                        Produto: {product?.title || "Produto não encontrado"}
                      </p>
                      <p>
                        Data:{" "}
                        {new Date(comment.created_at).toLocaleDateString(
                          "pt-BR"
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {!comment.is_approved && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleApprove(comment.id)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Aprovar
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(comment.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Settings Component
const SettingsManagement = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: "5511976881046",
    whatsapp_message_template:
      "Olá! Gostaria de fazer um pedido:\n\n{order_details}\n\nTotal: R$ {total}",
    delivery_fee: 5.0,
    minimum_order: 20.0,
    store_name: "Cardápio Bonfim",
    store_description: "Produtos gourmet de alta qualidade",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/settings`, settings);
      alert("Configurações salvas com sucesso!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Erro ao salvar configurações");
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900">Configurações</h2>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="store_name">Nome da Loja</Label>
                <Input
                  id="store_name"
                  value={settings.store_name}
                  onChange={(e) =>
                    setSettings({ ...settings, store_name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="whatsapp_number">Número do WhatsApp</Label>
                <Input
                  id="whatsapp_number"
                  value={settings.whatsapp_number}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      whatsapp_number: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formato: 55DDNNNNNNNNN (com código do país e área)
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="store_description">Descrição da Loja</Label>
              <Textarea
                id="store_description"
                value={settings.store_description}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    store_description: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <Label htmlFor="whatsapp_message">
                Template da Mensagem do WhatsApp
              </Label>
              <Textarea
                id="whatsapp_message"
                value={settings.whatsapp_message_template}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    whatsapp_message_template: e.target.value,
                  })
                }
                rows={5}
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {"{order_details}"} para os itens do pedido e {"{total}"}{" "}
                para o valor total
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="delivery_fee">Taxa de Entrega (R$)</Label>
                <Input
                  id="delivery_fee"
                  type="number"
                  step="0.01"
                  value={settings.delivery_fee}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      delivery_fee: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="minimum_order">Pedido Mínimo (R$)</Label>
                <Input
                  id="minimum_order"
                  type="number"
                  step="0.01"
                  value={settings.minimum_order}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      minimum_order: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                className=""
                style={{ background: "#c9332C" }}
                onMouseEnter={(e) => (e.target.style.background = "#a02820")}
                onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
              >
                Salvar Configurações
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Panel Component
// ...existing code...

// Main App Component
function App() {
  const { isOpen, title, message, type, confirmText, onConfirm, closeError } =
    useErrorStore();

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/print-order" element={<PrintOrderReceipt />} />
            <Route path="/*" element={<ProtectedRoute />} />
          </Routes>

          {/* Modal de Erro Global */}
          <ErrorModal
            open={isOpen}
            onClose={closeError}
            title={title}
            message={message}
            type={type}
            confirmText={confirmText}
            onConfirm={onConfirm}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Protected Route Component
const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <AdminPanel />;
};

// Login Route Component
const LoginRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" />;
  }

  return <Login />;
};

export default App;
